"use client"

import { useState } from "react"
import { Mail, ArrowLeft } from "lucide-react"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!res.ok) {
        const data = await res.json()
        setError(data.error || "Something went wrong. Please try again.")
        setIsLoading(false)
        return
      }

      setSent(true)
    } catch (err) {
      console.error('Forgot password error:', err)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Subtle background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full blur-[150px] bg-cyan-500/8" />
      </div>

      <div className="w-full max-w-[440px] relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-10 h-10" />
            <span className="font-semibold tracking-wide uppercase text-2xl hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-2xl inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">
            Forgot your password?
          </h1>
          <p className="text-muted-foreground">
            Enter your email and we&apos;ll send a reset link
          </p>
        </div>

        {/* Card */}
        <div className="relative p-8 rounded-3xl bg-card/80 dark:bg-white/[0.03] backdrop-blur-xl border border-border dark:border-white/[0.08] shadow-2xl">
          <div className="absolute inset-0 rounded-3xl opacity-50 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/10" />

          <div className="relative">
            {sent ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Mail className="w-7 h-7 text-cyan-400" />
                </div>
                <h2 className="text-lg font-semibold text-foreground mb-2">Check your email</h2>
                <p className="text-sm text-muted-foreground">
                  If an account exists with that email, a reset link has been sent.
                </p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full bg-gray-100 dark:bg-white/5 border border-border dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:border-cyan-500 focus:outline-none rounded-xl h-12 pl-11 pr-4 text-sm transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 shadow-lg shadow-cyan-500/25 transition-all duration-300 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <span>Send Reset Link</span>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Back to sign in */}
        <div className="mt-6 flex justify-center">
          <a
            href="/auth/signin"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to sign in</span>
          </a>
        </div>
      </div>
    </div>
  )
}
